import "./PersonalSiteCompanyInfo.css";
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Button, Empty, Form, Modal, Radio, Spin, Tag, Tooltip, message } from "antd";
import { useRequest } from "ahooks";
import { getCustomerId } from "Common";
import { getBranchColor } from "../../../BranchSwitcher/colors";
import {
  ORG_SWITCH_NOTICE_KEY,
  switchOrganization,
} from "../../../BranchSwitcher/switchOrganization";
import { request } from "../../../request";
import { track } from "statsig";
import { STATSIG } from "statsig/events";
import { expireAuthAndRedirectToLogin } from "../../../syncStrataSession.js";

import type { TypeProfileInfo } from "../../interface";

export type CompanyOption = {
  customerId: string;
  companyName: string;
  firstName?: string;
  lastName?: string;
  nmls?: string;
  address?: string;
  city?: string;
  county?: string;
  state?: string;
  zip?: string;
  companyNMLS?: string;
  link?: string;
  banner?: string;
  companyIntroduction?: string;
  primary?: boolean;
  marketplaceStatus?: number | null;
  initialized?: boolean;
  selectable?: boolean;
  disabledReason?: string;
  roles?: string[];
  organizationName?: string;
};

interface PersonalSiteCompanyInfoProps {
  company: TypeProfileInfo.Company;
  options?: CompanyOption[];
  optionsLoading?: boolean;
  selectedCustomerId?: string;
  onRefreshOptions?(): Promise<CompanyOption[] | void>;
}

export type PersonalSiteCompanyInfoHandle = {
  openPicker(): void;
};

function isUnavailableOrganizationError(error: unknown) {
  const text = error instanceof Error ? error.message : String(error || "");
  return /disabled/i.test(text);
}

const BLUERATE_PAGE_ROLE_KEYS = new Set([
  "loanofficer",
  "owner",
  "loanofficermanager",
]);

function normalizeRoleKey(role?: string) {
  return String(role || "").replace(/\s+/g, "").toLowerCase();
}

function companyOptionRoles(option?: Pick<CompanyOption, "roles">) {
  return (option?.roles || [])
    .map((role) => String(role || "").trim())
    .filter(Boolean);
}

export function optionOrganizationName(
  option?: Pick<CompanyOption, "organizationName" | "companyName">,
) {
  return String(option?.organizationName || option?.companyName || "").trim();
}

export function officerFullName(option?: Pick<CompanyOption, "firstName" | "lastName">) {
  return [option?.firstName, option?.lastName].filter(Boolean).join(" ").trim();
}

export function formatBluerateOrgLabel(
  orgName?: string,
  option?: Pick<CompanyOption, "firstName" | "lastName" | "companyName" | "organizationName">,
) {
  const org = String(orgName || optionOrganizationName(option) || "").trim();
  const officer = officerFullName(option);
  if (org && officer) return `${org} — ${officer}`;
  return org || officer;
}

export function isProcessorOnlyCompanyOption(option?: CompanyOption) {
  const roles = companyOptionRoles(option);
  if (!roles.length) return false;
  return roles.every((role) => {
    const normalized = normalizeRoleKey(role);
    return normalized === "loanprocessor" || normalized === "processor";
  });
}

export function hasBlueratePageRole(option?: CompanyOption) {
  const roles = companyOptionRoles(option);
  // Unknown roles must not block editing; get-branches merge fills this in.
  if (!roles.length) return true;
  return roles.some((role) => BLUERATE_PAGE_ROLE_KEYS.has(normalizeRoleKey(role)));
}

export function isReachableCompanyOption(
  customerId?: string,
  options: CompanyOption[] = [],
) {
  const id = String(customerId || "").trim();
  if (!id) return false;
  return options.some((item) => String(item.customerId || "").trim() === id);
}

export function canManageBluerateOption(option?: CompanyOption) {
  return hasBlueratePageRole(option);
}

export function canSwitchToManageBluerate(
  customerId?: string,
  options: CompanyOption[] = [],
) {
  const id = String(customerId || "").trim();
  if (!id || !isReachableCompanyOption(id, options)) return false;
  const option = options.find((item) => String(item.customerId || "").trim() === id);
  return canManageBluerateOption(option);
}

export function chooseNewOrganizationReason(
  customerId?: string,
  options: CompanyOption[] = [],
  orgName?: string,
) {
  const id = String(customerId || "").trim();
  const org = String(orgName || "").trim() || "that organization";
  if (!id) return "";
  if (!isReachableCompanyOption(id, options)) {
    return `${org} is no longer available, so you can't manage your Bluerate profile there. Choose a new organization.`;
  }
  const option = options.find((item) => String(item.customerId || "").trim() === id);
  if (isProcessorOnlyCompanyOption(option)) {
    return `Your role in ${org} is Loan processor, so you can't open Bluerate there. Choose a new organization to manage your profile.`;
  }
  if (!hasBlueratePageRole(option)) {
    return `Your current role in ${org} can't open Bluerate. Choose a new organization to manage your profile.`;
  }
  return "";
}

export function resolveBluerateCustomerID(
  formSourceCustomerID?: string,
  options: CompanyOption[] = [],
) {
  const formId = String(formSourceCustomerID || "").trim();
  // Options still loading — keep the form pointer so we do not briefly treat
  // primary-elsewhere as "edit current org" and flash the wrong preview empty.
  if (formId && !options.length) {
    return formId;
  }

  const formOption = options.find((item) => item.customerId === formId);
  // Keep the org the profile actually lives in, even if this user can no
  // longer open Bluerate there. Falling back to another org would show
  // "Managed in Goodview" and try to switch into the wrong place.
  if (formId && isReachableCompanyOption(formId, options) && isOptionInitialized(formOption)) {
    return formId;
  }

  const primaryInitialized = options.find(
    (item) => item.primary && isOptionInitialized(item),
  );
  if (primaryInitialized?.customerId) {
    return String(primaryInitialized.customerId).trim();
  }

  // Do not pick a random other org. That is how the editor used to fall
  // through to Goodview when the profile org was Processor-only.
  return "";
}

type BranchOrganization = {
  customerId?: string;
  companyName?: string;
  roles?: string[];
};

async function fetchBranchOrganizations(): Promise<BranchOrganization[]> {
  const token =
    typeof window !== "undefined"
      ? window.sessionStorage.getItem("ZeitroA")
      : "";
  if (!token) return [];
  try {
    const res = await fetch("/api/v1/los/auth/get-branches", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
      expireAuthAndRedirectToLogin();
      return [];
    }
    const data = await res.json();
    if (data?.code === 0 && Array.isArray(data.data)) return data.data;
  } catch {
    // Branch roles are a fallback; company-options can still render.
  }
  return [];
}

function mergeCompanyOptionRoles(
  options: CompanyOption[],
  branches: BranchOrganization[],
) {
  const byId = new Map<string, BranchOrganization>();
  for (const branch of branches) {
    const id = String(branch.customerId || "").trim();
    if (id) byId.set(id, branch);
  }
  return options.map((item) => {
    const branch = byId.get(String(item.customerId || "").trim());
    const branchRoles = (branch?.roles || [])
      .map((role) => String(role || "").trim())
      .filter(Boolean);
    const next: CompanyOption = {
      ...item,
      roles: branchRoles.length ? branchRoles : item.roles,
      organizationName:
        String(branch?.companyName || "").trim() ||
        item.organizationName ||
        item.companyName,
    };
    if (!hasBlueratePageRole(next)) {
      next.selectable = false;
      next.disabledReason = isProcessorOnlyCompanyOption(next)
        ? "You can't open Bluerate in this organization because your role is Loan processor. Choose another organization."
        : "You can't open Bluerate in this organization with your current role. Choose another organization.";
    }
    return next;
  });
}

export async function fetchPersonalSiteCompanyOptions() {
  const [res, branches] = await Promise.all([
    request("/los/personal-site-company-options") as Promise<
      CompanyOption[] | { organizations?: CompanyOption[] }
    >,
    fetchBranchOrganizations(),
  ]);
  const options = Array.isArray(res)
    ? res
    : Array.isArray(res?.organizations)
      ? res.organizations
      : null;
  if (!options) throw new Error("Failed to load personal site company options");
  return mergeCompanyOptionRoles(options, branches);
}

type LandingCompanyField =
  | "BluerateCompanySourceCustomerID"
  | "BluerateCompanyName"
  | "BluerateCompanyAddress"
  | "BluerateCompanyCity"
  | "BluerateCompanyCounty"
  | "BluerateCompanyState"
  | "BluerateCompanyZip"
  | "BluerateCompanyNMLS"
  | "BluerateCompanyLink"
  | "BluerateBanner"
  | "BluerateCompanyIntroduction";

type LandingFieldWriters = Partial<
  Record<LandingCompanyField, (value: unknown) => void>
>;

const LANDING_COMPANY_FIELDS: LandingCompanyField[] = [
  "BluerateCompanySourceCustomerID",
  "BluerateCompanyName",
  "BluerateCompanyAddress",
  "BluerateCompanyCity",
  "BluerateCompanyCounty",
  "BluerateCompanyState",
  "BluerateCompanyZip",
  "BluerateCompanyNMLS",
  "BluerateCompanyLink",
  "BluerateBanner",
  "BluerateCompanyIntroduction",
];

const DEMO_COMPANY_OPTIONS: CompanyOption[] = [
  {
    customerId: "gmcc",
    companyName: "GMCC — Palo Alto",
    address: "328 University Ave, Palo Alto, CA 94301",
    city: "Palo Alto",
    state: "CA",
    zip: "94301",
    companyNMLS: "1902334",
    firstName: "Qi",
    lastName: "Yu",
    nmls: "456789",
    initialized: true,
  },
  {
    customerId: "gmcc-sanjose",
    companyName: "GMCC — San Jose",
    address: "84 W Santa Clara St, San Jose, CA 95113",
    city: "San Jose",
    state: "CA",
    zip: "95113",
    companyNMLS: "1902334",
    firstName: "Qi",
    lastName: "Yu",
    nmls: "456789",
    initialized: true,
  },
  {
    customerId: "gmcc-fremont",
    companyName: "GMCC — Fremont",
    address: "39465 Paseo Padre Pkwy, Fremont, CA 94538",
    city: "Fremont",
    state: "CA",
    zip: "94538",
    companyNMLS: "1902334",
    firstName: "Qi",
    lastName: "Yu",
    nmls: "456789",
    initialized: true,
  },
];

const MOCK_COMPANY_FLAG = "mockPersonalSiteCompanyOptions";

function isPersonalSiteCompanyMockEnabled() {
  if (process.env.NODE_ENV !== "development") return false;
  try {
    if (window.localStorage.getItem(MOCK_COMPANY_FLAG) === "1") return true;
  } catch {
    // Ignore storage access errors and fall through to the query flag.
  }
  return new URLSearchParams(window.location.search).get(MOCK_COMPANY_FLAG) === "1";
}

function optionFromCurrentCompany(company: TypeProfileInfo.Company): CompanyOption {
  return {
    customerId: "",
    companyName: company?.Name || "Current organization",
    address: company?.Address,
    city: company?.City,
    county: company?.County,
    state: company?.State,
    zip: company?.Zip,
    companyNMLS: company?.CompanyNMLS,
    link: company?.Link,
    banner: company?.Banner,
    companyIntroduction: company?.Introduction,
  };
}

function optionFromLanding(
  company: TypeProfileInfo.Company,
  landing: Partial<TypeProfileInfo.DTO["LandingPageSetUp"]>,
  sourceCustomerID?: string,
): CompanyOption {
  const fallback = optionFromCurrentCompany(company);
  return {
    ...fallback,
    customerId: sourceCustomerID || fallback.customerId,
    companyName: landing.BluerateCompanyName || fallback.companyName,
    address: landing.BluerateCompanyAddress || fallback.address,
    city: landing.BluerateCompanyCity || fallback.city,
    county: landing.BluerateCompanyCounty || fallback.county,
    state: landing.BluerateCompanyState || fallback.state,
    zip: landing.BluerateCompanyZip || fallback.zip,
    companyNMLS: landing.BluerateCompanyNMLS || fallback.companyNMLS,
    link: landing.BluerateCompanyLink || fallback.link,
    banner: landing.BluerateBanner || fallback.banner,
    companyIntroduction:
      landing.BluerateCompanyIntroduction || fallback.companyIntroduction,
  };
}

function landingValuesFromOption(
  option: CompanyOption,
): Partial<Record<LandingCompanyField, unknown>> {
  return {
    BluerateCompanySourceCustomerID: option.customerId,
    BluerateCompanyName: option.companyName,
    BluerateCompanyAddress: option.address,
    BluerateCompanyCity: option.city,
    BluerateCompanyCounty: option.county,
    BluerateCompanyState: option.state,
    BluerateCompanyZip: option.zip,
    BluerateCompanyNMLS: option.companyNMLS,
    BluerateCompanyLink: option.link,
    BluerateBanner: option.banner,
    BluerateCompanyIntroduction: option.companyIntroduction,
  };
}

function LandingCompanyFieldControl({
  field,
  writers,
  onChange,
}: {
  field: LandingCompanyField;
  writers: React.MutableRefObject<LandingFieldWriters>;
  value?: unknown;
  onChange?: (value: unknown) => void;
}) {
  writers.current[field] = onChange;
  return null;
}

export function organizationInitials(name?: string) {
  return (name || "Company")
    .split(/\s+|—|-/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export type BluerateSetupHint = {
  currentOrgName: string;
  profileOrg: CompanyOption;
  profileOrgName: string;
  profileOrgShort: string;
  profileOrgColor: string;
};

/**
 * When the current org still needs Personal site setup, but Bluerate already
 * exists in another organization, the wizard should point users there.
 */
export function resolveBluerateSetupHint(
  options: CompanyOption[] = [],
  currentCustomerId?: string,
  currentOrgNameFallback?: string,
): BluerateSetupHint | null {
  const currentId = String(currentCustomerId || "").trim();
  if (!currentId || !options.length) return null;

  const initializedElsewhere = options.filter(
    (item) =>
      isOptionInitialized(item) &&
      hasBlueratePageRole(item) &&
      String(item.customerId || "").trim() !== currentId,
  );
  if (!initializedElsewhere.length) return null;

  const profileOrg =
    initializedElsewhere.find((item) => item.primary) ||
    initializedElsewhere[0];
  const currentOption = options.find(
    (item) => String(item.customerId || "").trim() === currentId,
  );
  const profileIndex = options.findIndex(
    (item) => item.customerId === profileOrg.customerId,
  );

  return {
    currentOrgName:
      optionOrganizationName(currentOption) ||
      String(currentOrgNameFallback || "").trim() ||
      currentId,
    profileOrg,
    profileOrgName: optionOrganizationName(profileOrg) || profileOrg.customerId,
    profileOrgShort: organizationInitials(optionOrganizationName(profileOrg)),
    profileOrgColor: getBranchColor(profileIndex >= 0 ? profileIndex : 0),
  };
}

function detailsLine(option?: CompanyOption) {
  if (!option) return "";
  return [option.address, option.companyNMLS ? `Company NMLS #${option.companyNMLS}` : ""]
    .filter(Boolean)
    .join(" · ");
}

function hasCompanyAddress(option?: CompanyOption) {
  return Boolean(option?.address?.trim());
}

export function optionMarketplaceStatus(option?: CompanyOption) {
  if (typeof option?.marketplaceStatus === "number") return option.marketplaceStatus;
  if (option?.initialized) return 2;
  return 0;
}

export function isOptionInitialized(option?: CompanyOption) {
  return optionMarketplaceStatus(option) > 0;
}

/** Preview iframe once the org has a Bluerate listing (waiting / live / unpublished). */
export function isBlueratePreviewReady(option?: CompanyOption) {
  return isOptionInitialized(option);
}

export function blueratePreviewEmptyDescription(
  orgLabel: string,
  {
    canEdit = true,
    cannotManage = false,
  }: { canEdit?: boolean; cannotManage?: boolean } = {},
) {
  const org = String(orgLabel || "").trim() || "this organization";
  if (cannotManage) {
    return {
      before: `You can't preview this Bluerate profile because your role in ${org} is Loan processor. Choose a new organization to manage it.`,
      linkText: "",
      after: "",
      needsSwitch: false,
    };
  }
  if (canEdit) {
    return {
      before: `Bluerate for ${org} has not been set up yet. Click Complete setup & publish to initialize it before previewing.`,
      linkText: "",
      after: "",
      needsSwitch: false,
    };
  }
  return {
    before: `Bluerate for ${org} has not been set up yet. `,
    linkText: "Switch to that organization",
    after: " and complete setup to preview.",
    needsSwitch: true,
  };
}

function isOptionSelectable(option?: CompanyOption) {
  if (!option) return false;
  if (!hasBlueratePageRole(option)) return false;
  if (typeof option.selectable === "boolean") return option.selectable;
  return isOptionInitialized(option) && hasCompanyAddress(option);
}

function optionDisabledReason(option?: CompanyOption) {
  if (!option) return "";
  if (!hasBlueratePageRole(option)) {
    return isProcessorOnlyCompanyOption(option)
      ? "You can't open Bluerate in this organization because your role is Loan processor. Choose another organization."
      : "You can't open Bluerate in this organization with your current role. Choose another organization.";
  }
  if (!isOptionInitialized(option)) {
    return "You can't open Bluerate in this organization because it hasn't been set up yet. Switch to this organization to complete setup first.";
  }
  if (optionMarketplaceStatus(option) === 1) {
    return "Personal information in this organization is waiting for review and cannot be selected until it is approved.";
  }
  if (option.disabledReason) return option.disabledReason;
  if (!hasCompanyAddress(option)) {
    return "This organization has no company address. Ask an organization admin to add one first.";
  }
  return "";
}

const PersonalSiteCompanyInfo = React.forwardRef<
  PersonalSiteCompanyInfoHandle,
  PersonalSiteCompanyInfoProps
>(({
  company,
  options: optionsFromParent,
  optionsLoading,
  selectedCustomerId,
  onRefreshOptions,
}, ref) => {
  const form = Form.useFormInstance<TypeProfileInfo.DTO>();
  const sectionRef = useRef<HTMLElement>(null);
  const landingFieldWriters = useRef<LandingFieldWriters>({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRefreshing, setPickerRefreshing] = useState(false);
  const [draftCustomerID, setDraftCustomerID] = useState<string>();
  const [switchingOrg, setSwitchingOrg] = useState(false);
  const [unavailableOrgName, setUnavailableOrgName] = useState("");
  const [unavailableReason, setUnavailableReason] = useState("");
  const currentCustomerID = String(getCustomerId() || "").trim();
  const bluerateSourceCustomerID = Form.useWatch(
    ["LandingPageSetUp", "BluerateCompanySourceCustomerID"],
    form,
  );
  const landing = (Form.useWatch("LandingPageSetUp", form) ||
    {}) as Partial<TypeProfileInfo.DTO["LandingPageSetUp"]>;

  const { data, loading: fetchedLoading, refreshAsync: refreshFetched } = useRequest(
    fetchPersonalSiteCompanyOptions,
    { ready: optionsFromParent == null },
  );
  const loading = optionsFromParent != null ? !!optionsLoading : fetchedLoading;
  const pickerLoading = pickerRefreshing || (pickerOpen && loading);
  const fetchedOptions = (optionsFromParent ?? data ?? []) as CompanyOption[];

  const bluerateFallbackOption = useMemo<CompanyOption>(
    () =>
      optionFromLanding(
        company,
        landing,
        bluerateSourceCustomerID,
      ),
    [company, landing, bluerateSourceCustomerID],
  );

  const options = useMemo<CompanyOption[]>(() => {
    const list = fetchedOptions.length
      ? fetchedOptions
      : loading
        ? []
        : isPersonalSiteCompanyMockEnabled()
          ? DEMO_COMPANY_OPTIONS
          : [];
    return [...list].sort(
      (a, b) => Number(isOptionSelectable(b)) - Number(isOptionSelectable(a)),
    );
  }, [fetchedOptions, loading]);

  const selectedCustomerID = resolveBluerateCustomerID(
    selectedCustomerId ?? bluerateSourceCustomerID,
    options,
  );

  const primaryOption = useMemo<CompanyOption | undefined>(() => {
    if (!selectedCustomerID) return undefined;
    return (
      options.find((item) => item.customerId === selectedCustomerID) ||
      (isReachableCompanyOption(bluerateSourceCustomerID, options)
        ? bluerateFallbackOption
        : undefined)
    );
  }, [
    bluerateFallbackOption,
    bluerateSourceCustomerID,
    options,
    selectedCustomerID,
  ]);

  const profileLivesElsewhere = Boolean(
    selectedCustomerID &&
      String(selectedCustomerID).trim() !== currentCustomerID,
  );
  const primaryOrgName = optionOrganizationName(primaryOption) || selectedCustomerID;
  const primaryOrgLabel = formatBluerateOrgLabel(primaryOrgName, primaryOption);
  const pointedCustomerID = String(
    bluerateSourceCustomerID || selectedCustomerId || "",
  ).trim();
  const pointedOption = options.find(
    (item) => String(item.customerId || "").trim() === pointedCustomerID,
  );
  const managedOrgMissing = Boolean(
    !loading &&
      pointedCustomerID &&
      pointedCustomerID !== currentCustomerID &&
      !isReachableCompanyOption(pointedCustomerID, options),
  );
  const managedOrgUnmanageable = Boolean(
    !loading && primaryOption && !canManageBluerateOption(primaryOption),
  );
  const staleManagedOrgName = String(
    optionOrganizationName(pointedOption) ||
      landing.BluerateCompanyName ||
      primaryOrgName ||
      pointedCustomerID,
  ).trim();
  const showUnavailableOrgNotice = Boolean(
    unavailableOrgName || managedOrgMissing || managedOrgUnmanageable,
  );
  // Single org, not yet Complete setup — no primary to pick; nudge to publish.
  const soleOrgNeedsSetup = Boolean(
    !loading &&
      options.length === 1 &&
      !primaryOption &&
      !isOptionInitialized(options[0]),
  );
  // Hide Change when only one org is already primary; hide Select when
  // the only org still needs Complete setup & publish.
  const showChangeOrSelectPrimary =
    !soleOrgNeedsSetup &&
    (loading || !primaryOption || options.length > 1);
  const chooseNewOrgTooltip =
    unavailableReason ||
    chooseNewOrganizationReason(
      pointedCustomerID,
      options,
      staleManagedOrgName,
    );

  useEffect(() => {
    const notice = sessionStorage.getItem(ORG_SWITCH_NOTICE_KEY);
    if (!notice) return;
    sessionStorage.removeItem(ORG_SWITCH_NOTICE_KEY);
    message.success(notice);
  }, []);

  useEffect(() => {
    if (!managedOrgMissing) return;
    setUnavailableOrgName((prev) => prev || staleManagedOrgName);
    setUnavailableReason(
      (prev) =>
        prev ||
        chooseNewOrganizationReason(
          pointedCustomerID,
          options,
          staleManagedOrgName,
        ),
    );
    landingFieldWriters.current.BluerateCompanySourceCustomerID?.("");
  }, [managedOrgMissing, pointedCustomerID, options, staleManagedOrgName]);

  useEffect(() => {
    if (!managedOrgUnmanageable) return;
    setUnavailableOrgName((prev) => prev || staleManagedOrgName);
    setUnavailableReason(
      (prev) =>
        prev ||
        chooseNewOrganizationReason(
          pointedCustomerID,
          options,
          staleManagedOrgName,
        ),
    );
  }, [managedOrgUnmanageable, pointedCustomerID, options, staleManagedOrgName]);

  function getCompanyColor(option?: CompanyOption) {
    const index = options.findIndex((item) =>
      option?.customerId
        ? item.customerId === option.customerId
        : item.companyName === option?.companyName,
    );
    return getBranchColor(index >= 0 ? index : 0);
  }

  function applyOption(option: CompanyOption) {
    const nextValues = landingValuesFromOption(option);
    (Object.keys(nextValues) as LandingCompanyField[]).forEach((field) => {
      landingFieldWriters.current[field]?.(nextValues[field]);
    });
    setUnavailableOrgName("");
    setUnavailableReason("");
    sectionRef.current
      ?.closest("form")
      ?.dispatchEvent(new MouseEvent("mousemove", { bubbles: true }));
  }

  async function refreshPickerOptions() {
    try {
      if (onRefreshOptions) {
        await onRefreshOptions();
        return;
      }
      await refreshFetched();
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : "Failed to load organizations",
      );
    }
  }

  function openPicker() {
    if (loading) return;
    setPickerOpen(true);
    setDraftCustomerID(primaryOption?.customerId);
    setPickerRefreshing(true);
    void refreshPickerOptions().finally(() => setPickerRefreshing(false));
  }

  useImperativeHandle(ref, () => ({ openPicker }), [primaryOption?.customerId]);

  function closePicker() {
    setPickerOpen(false);
    setPickerRefreshing(false);
    setDraftCustomerID(undefined);
  }

  async function goToPrimaryOrganization() {
    const targetId = primaryOption?.customerId || selectedCustomerID;
    if (!targetId || !canSwitchToManageBluerate(targetId, options)) {
      openPicker();
      return;
    }
    try {
      setSwitchingOrg(true);
      await switchOrganization(targetId, {
        redirectTo: "/app/landingpage",
        notice: `Switched to ${primaryOrgName} — opened its Bluerate settings`,
      });
    } catch (error) {
      setSwitchingOrg(false);
      if (isUnavailableOrganizationError(error)) {
        message.warning(
          "That organization is no longer available. Choose a new one to manage your Bluerate profile.",
        );
        openPicker();
        return;
      }
      message.error(
        error instanceof Error ? error.message : "Failed to switch organization",
      );
    }
  }

  function savePick() {
    const nextOption = options.find((item) => item.customerId === draftCustomerID);
    if (!nextOption) {
      closePicker();
      return;
    }

    if (!isOptionSelectable(nextOption)) {
      message.warning(
        optionDisabledReason(nextOption) ||
          "This organization cannot be selected.",
      );
      return;
    }

    applyOption(nextOption);
    track(STATSIG.PERSONAL_SITE.BLUERATE_ORG, "update", { type: "update" });
    closePicker();
  }

  return (
    <section
      ref={sectionRef}
      className="layout-profile-ctx personal-site-company-info"
    >
      {LANDING_COMPANY_FIELDS.map((field) => (
        <Form.Item key={field} hidden noStyle name={["LandingPageSetUp", field]}>
          <LandingCompanyFieldControl field={field} writers={landingFieldWriters} />
        </Form.Item>
      ))}
      <div className="personal-site-company-profile">
        <p>Bluerate profile</p>
        <p>
          One NMLS = one profile. Its company info always follows your{" "}
          <strong>primary organization</strong>.
        </p>
        {loading ? (
          <div className="personal-site-company-profile-loading">
            <Spin size="small" />
          </div>
        ) : showUnavailableOrgNotice ? (
          <div className="personal-site-company-elsewhere">
            <p>
              Your Bluerate profile was in{" "}
              <strong>
                {unavailableOrgName || staleManagedOrgName || "another organization"}
              </strong>
              , which you can no longer manage. Choose a new organization to
              manage it here.
            </p>
            <Tooltip title={chooseNewOrgTooltip || undefined} placement="topLeft">
              <Button type="link" onClick={openPicker}>
                Choose a new organization →
              </Button>
            </Tooltip>
          </div>
        ) : profileLivesElsewhere ? (
          <div className="personal-site-company-elsewhere">
            <p>
              You can only have one Bluerate profile — yours lives in{" "}
              <strong>{primaryOrgLabel}</strong>.
            </p>
            <Button
              type="link"
              loading={switchingOrg}
              onClick={goToPrimaryOrganization}
            >
              {`Edit it in ${primaryOrgLabel} →`}
            </Button>
          </div>
        ) : (
          <div className="personal-site-company-current-row">
            {primaryOption ? (
              <CompanyChip
                compact
                option={primaryOption}
                badge="Primary"
                color={getCompanyColor(primaryOption)}
              />
            ) : (
              <span className="personal-site-company-unselected">
                {soleOrgNeedsSetup
                  ? "Complete setup & publish to set your primary organization."
                  : "No primary organization selected"}
              </span>
            )}
            {showChangeOrSelectPrimary ? (
              <Button type="link" onClick={openPicker}>
                {primaryOption
                  ? "Change primary organization"
                  : "Select primary organization"}
              </Button>
            ) : null}
          </div>
        )}
      </div>

      <Modal
        open={pickerOpen}
        width={560}
        destroyOnClose={false}
        title={
          primaryOption
            ? "Change primary organization"
            : "Select primary organization"
        }
        onCancel={closePicker}
        onOk={savePick}
        okText="Save"
        okButtonProps={{
          disabled:
            pickerLoading ||
            !isOptionSelectable(
              options.find((item) => item.customerId === draftCustomerID),
            ),
        }}
        cancelText="Cancel"
        className="personal-site-company-modal"
      >
        <p className="personal-site-company-modal-caption">
          Your Bluerate profile shows the selected primary organization as your company.
        </p>
        <Spin spinning={pickerLoading}>
          {options.length ? (
            <Radio.Group
              value={draftCustomerID}
              className="personal-site-company-options"
              onChange={(event) => setDraftCustomerID(event.target.value)}
            >
              {options.map((option, index) => {
                const selectable = isOptionSelectable(option);
                return (
                  <Radio
                    className={`personal-site-company-option${
                      selectable ? "" : " personal-site-company-option-disabled"
                    }`}
                    disabled={!selectable}
                    key={option.customerId}
                    value={option.customerId}
                  >
                    <CompanyChip
                      option={option}
                      badge={
                        option.customerId === primaryOption?.customerId
                          ? "Primary"
                          : undefined
                      }
                      color={getBranchColor(index)}
                      disabledReason={
                        selectable ? "" : optionDisabledReason(option)
                      }
                    />
                  </Radio>
                );
              })}
            </Radio.Group>
          ) : (
            <Empty description="No organizations available" />
          )}
        </Spin>
        <p className="personal-site-company-modal-warning">
          Changing your primary organization immediately updates the company info on your
          Bluerate profile and is logged for audit.
        </p>
      </Modal>
    </section>
  );
});

function CompanyChip({
  option,
  badge,
  badgeStatus = "primary",
  compact = false,
  color = getBranchColor(0),
  disabledReason = "",
}: {
  option?: CompanyOption;
  badge?: string;
  badgeStatus?: "primary" | "warning";
  compact?: boolean;
  color?: string;
  disabledReason?: string;
}) {
  return (
    <div className={`personal-site-company-chip ${compact ? "personal-site-company-chip-compact" : ""}`}>
      <div className="personal-site-company-avatar" style={{ background: color }}>
        {organizationInitials(optionOrganizationName(option))}
      </div>
      <div className="personal-site-company-chip-copy">
        <div className="personal-site-company-chip-name">
          <strong>{optionOrganizationName(option) || "Company info"}</strong>
          {badge ? (
            <Tag className={`personal-site-company-tag personal-site-company-tag-${badgeStatus}`}>
              {badge}
            </Tag>
          ) : null}
        </div>
        {!compact ? (
          <span>{detailsLine(option) || "No company address yet"}</span>
        ) : null}
        {disabledReason ? (
          <span className="personal-site-company-missing-address">
            {disabledReason}
          </span>
        ) : null}
      </div>
    </div>
  );
}

PersonalSiteCompanyInfo.displayName = "PersonalSiteCompanyInfo";

export default PersonalSiteCompanyInfo;
